import React, { useState, useRef, ChangeEvent } from 'react';
import { FileSpreadsheet, Upload, Info, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx-republish';
import { supabase } from '../../lib/supabase';
import { generateToken } from '../../utils/token';

interface ExcelImportSectionProps {
  onImportComplete?: () => void;
  onProcessXLSX?: (file: File) => Promise<void>;
}

const ExcelImportSection = ({ 
  onImportComplete,
  onProcessXLSX
}: ExcelImportSectionProps): React.ReactElement => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewData, setPreviewData] = useState<unknown[][]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setError(null);
    setPreviewData([]);
    setIsUploading(true);
    
    
    // Check file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !['xlsx', 'xls', 'csv'].includes(fileExtension)) {
      setError("Type de fichier non supporté. Veuillez utiliser un fichier Excel (.xlsx, .xls) ou CSV.");
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsUploading(false);
      return;
    }
    
    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Le fichier est trop volumineux. La taille maximale autorisée est de 5 Mo.");
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsUploading(false);
      return;
    }
    
    // If there's a custom process function, use it directly
    if (onProcessXLSX) {
      onProcessXLSX(file)
        .then(() => {
          setIsUploading(false);
          setError("✅ Fichier importé avec succès!");
          if (fileInputRef.current) fileInputRef.current.value = '';
          if (onImportComplete) onImportComplete();
        })
        .catch(err => {
          console.error('Error in custom process:', err);
          setError(err instanceof Error ? err.message : "Erreur lors de l'importation");
          setIsUploading(false);
        });
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        if (!e.target || !e.target.result) {
          throw new Error("Impossible de lire le fichier");
        }
        
        
        // Parse the file based on extension
        let workbook;
        try {
          const result = e.target.result;
          
          if (typeof result === 'string') {
            // Handle as binary string
            workbook = XLSX.read(result, { type: 'binary' });
          } else if (result instanceof ArrayBuffer) {
            // Handle as array buffer
            // Important fix: Create a proper Uint8Array view of the buffer
            const arr = new Uint8Array(result);
            workbook = XLSX.read(arr, { type: 'array' });
          } else {
            console.error('Unexpected result type:', result);
            throw new Error("Format de fichier non reconnu");
          }
          
        } catch (parseError: unknown) {
          console.error('XLSX parse error:', parseError);
          const errorMessage = parseError instanceof Error ? parseError.message : 'Erreur inconnue';
          throw new Error(`Erreur lors de l'analyse du fichier: ${errorMessage}`);
        }
        
        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          throw new Error("Le fichier Excel ne contient aucune feuille");
        }
        
        const worksheet = workbook.Sheets[firstSheetName];
        if (!worksheet) {
          throw new Error("Impossible de lire la feuille Excel");
        }
        
        // Try different methods to extract data
        let jsonData;
        
        // Let's try multiple approaches to handle various Excel file formats
        let extractionSuccess = false;
        
        // Approach 1: Try getting the sheet range to understand the data dimensions
        try {
          const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
          
          // If the sheet has data (more than just A1 cell)
          if (range.e.r > 0 || range.e.c > 0) {
            // First try the most reliable method - aoa for array of arrays
            jsonData = XLSX.utils.sheet_to_json<Array<any>>(worksheet, { 
              header: 1,
              defval: null,
              blankrows: false,
              raw: false
            });
            
            
            if (jsonData && Array.isArray(jsonData) && jsonData.length > 0) {
              extractionSuccess = true;
            }
          }
        } catch (rangeErr) {
          console.error('Error reading sheet range:', rangeErr);
        }
        
        // Approach 2: If the first approach didn't work, try standard object approach
        if (!extractionSuccess) {
          try {
            const objData = XLSX.utils.sheet_to_json(worksheet, {
              raw: false,
              defval: '',
              blankrows: false
            });
            
            
            if (objData && Array.isArray(objData) && objData.length > 0) {
              // Convert object format to array format with headers
              const headers = Object.keys(objData[0] || {});
              // Use a type assertion for the entire array before mapping
              const typedObjData = objData as Record<string, any>[];
              const rows = typedObjData.map(row => headers.map(h => row[h] || ''));
              jsonData = [headers, ...rows];
              extractionSuccess = true;
            }
          } catch (objErr) {
            console.error('Error with object extraction:', objErr);
          }
        }
        
        // Approach 3: Last resort - CSV extraction
        if (!extractionSuccess) {
          try {
            const csvStr = XLSX.utils.sheet_to_csv(worksheet);
            if (csvStr && csvStr.length > 0) {
              // Parse the CSV string
              const rows = csvStr.split('\n')
                .filter(row => row && row.trim().length > 0)
                .map(row => row.split(','));
              
              if (rows.length > 0) {
                jsonData = rows;
                extractionSuccess = true;
              }
            }
          } catch (csvErr) {
            console.error('Error with CSV extraction:', csvErr);
          }
        }
        
        // If all extraction methods failed, create a minimal dataset for debugging
        if (!extractionSuccess) {
          console.error('All extraction methods failed! Creating minimal dataset for debugging');
          // Create a minimal dataset with an email_emelia column for testing
          jsonData = [
            ['email_emelia', 'firstName', 'lastName', 'company'],
            ['test@example.com', 'Test', 'User', 'Test Company']
          ];
          // Show error to user but continue with minimal dataset
          setError("Le format du fichier n'a pas pu être reconnu. Un jeu de données minimal a été créé pour vous permettre de tester l'importation.");
        }

        // Validate data structure
        if (!Array.isArray(jsonData) || jsonData.length === 0) {
          console.error('No valid data found in file');
          throw new Error("Format de fichier invalide ou aucune donnée présente");
        }
        

        // Process the data based on its format
        let filteredData;
        
        // If data is in row format (with headers as first row)
        if (Array.isArray(jsonData[0])) {
          // Filter out completely empty rows
          filteredData = jsonData.filter((row: unknown) =>
            Array.isArray(row) && row.some((cell: unknown) => cell !== null && cell !== '')
          );
        } else {
          // Data is in object format with keys
          // Convert to array format with headers
          // Safe type assertion since we verified jsonData is an array and has at least one element
          const firstRow = jsonData[0] as Record<string, unknown>;
          const headers = Object.keys(firstRow || {});
          // Type assertion for jsonData as it's already validated to be an array
          const dataRows = (jsonData as Record<string, unknown>[]).map(row => headers.map(h => row[h]));
          filteredData = [headers, ...dataRows];
        }
        
        if (filteredData.length === 0) {
          console.error('No data after filtering');
          throw new Error("Le fichier ne contient aucune donnée utilisable");
        }

        // Check if we have at least a header row and data row
        // Cast filteredData to any[][] since we know it's an array by now
        const typedFilteredData = filteredData as any[][];
        if (typedFilteredData.length < 2) {
          // Try to be more flexible - if we have at least one row, use it as header
          if (typedFilteredData.length === 1) {
            // Insert an empty data row to prevent errors
            const headerRow = typedFilteredData[0];
            const emptyRow = Array(headerRow ? headerRow.length : 0).fill('');
            typedFilteredData.push(emptyRow);
          } else {
            throw new Error("Le fichier doit contenir au moins un en-tête et une ligne de données");
          }
          // Reassign back to filteredData
          filteredData = typedFilteredData;
        }

        // Get headers (first row)
        const headers = filteredData[0];
        if (!Array.isArray(headers)) {
          console.error('Invalid headers type:', typeof headers, headers);
          throw new Error("L'en-tête du fichier est invalide");
        }
        

        // More flexible email column detection - allow either email_emelia or email
        const hasEmailColumn = headers.some(header => {
          // Convert header to string if it's not already
          const headerStr = String(header).toLowerCase();
          return headerStr.includes('email') || headerStr === 'e-mail' || headerStr === 'mail';
        });
        
        if (!hasEmailColumn) {
          console.error('No email column found in headers:', headers);
          throw new Error("Le fichier doit contenir une colonne d'email (email, email_emelia, e-mail, etc.)");
        }

        // Validate that we have actual data rows
        const dataRows = filteredData.slice(1);
        if (dataRows.length === 0) {
          console.warn("File has no data rows after header");
          // Add an empty row for preview purposes
          filteredData.push(Array(headers.length).fill(''));
        }

        // Ensure we have valid data structure
        if (filteredData.length > 0) {
          // Make sure filteredData is the right type before setting state
          const typedData: unknown[][] = filteredData.map((row: unknown) =>
            Array.isArray(row) ? row : [row]
          );
          
          // Store the full dataset for import
          setFullData(typedData);
          
          // Set preview data (first 10 rows including header)
          const previewCount = Math.min(10, typedData.length);
          setPreviewData(typedData.slice(0, previewCount));
          
          // Log the total number of rows for debugging
          const totalRowCount = typedData.length - 1; // Subtract 1 for header row
          setError(`${totalRowCount} prospects trouvés dans le fichier. Aperçu des ${previewCount - 1} premiers ci-dessous.`);
        } else {
          throw new Error("Aucune donnée valide dans le fichier");
        }
        
      } catch (err) {
        console.error('Error parsing Excel file:', err);
        // Log full error details for debugging
        setError(err instanceof Error ? err.message : "Erreur lors de la lecture du fichier Excel");
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } finally {
        setIsUploading(false);
      }
    };
    
    reader.onerror = () => {
      setError("Erreur lors de la lecture du fichier");
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    
    // For users experiencing issues with file upload, implement a direct approach
    try {
      // Direct the user to try a simpler Excel file
      setError('Importation du fichier Excel en cours... Si cette tentative échoue, essayez de simplifier le format de votre fichier Excel (évitez les formules complexes et les formats spéciaux).');
      
      // Try multiple approaches for better compatibility
      const extension = file.name.split('.').pop()?.toLowerCase();
      
      if (extension === 'csv') {
        reader.readAsText(file);
      } else {
        // For Excel files, first try as ArrayBuffer
        try {
          reader.readAsArrayBuffer(file);
        } catch (innerErr) {
          console.error('Error with ArrayBuffer, falling back to BinaryString:', innerErr);
          try {
            reader.readAsBinaryString(file);
          } catch (binaryErr) {
            console.error('Error with BinaryString, falling back to DataURL:', binaryErr);
            reader.readAsDataURL(file);
          }
        }
      }
    } catch (err) {
      console.error('Error starting file read:', err);
      setError('Erreur lors de la lecture du fichier - essayez un autre format de fichier Excel (.xlsx ou .xls simple)');
      setIsUploading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  const [fullData, setFullData] = useState<any[][]>([]);

  const handleImport = async () => {
    if (!fullData || fullData.length <= 1) {
      setError("Aucune donnée à importer");
      return;
    }

    // Check if email column exists using a more flexible approach
    const headers = fullData[0];
    
    // Look for any email-like column
    const hasEmailColumn = headers.some(header => {
      if (!header) return false;
      const headerStr = String(header).toLowerCase();
      return headerStr.includes('email') || headerStr === 'e-mail' || headerStr === 'mail';
    });
    
    if (!hasEmailColumn) {
      setError("Une colonne d'email est requise dans le fichier (email, email_emelia, e-mail, etc.)");
      return;
    }
    
    setIsUploading(true);
    setError(null);
    
    try {
      // If parent provided a custom processor, use it
      if (onProcessXLSX && fileInputRef.current?.files?.[0]) {
        await onProcessXLSX(fileInputRef.current.files[0]);
        alert(`✅ Données importées avec succès !`);
      } else {
        // Use internal processing logic
        // Get object data from headers and values
        const [headers, ...dataRows] = fullData;
        
        const prospects = dataRows.map(row => {
          // Create an object mapping headers to values
          const rowData = headers.reduce((acc, header, index) => {
            if (header) { // Only add the header if it's a valid string
              const headerKey = String(header);
              // Handle empty cells properly by using null instead of empty string
              acc[headerKey] = (row[index] !== undefined && row[index] !== null) ? row[index] : null;
            }
            return acc;
          }, {} as Record<string, any>);

          // Look for any email field (email_emelia, email, etc.)
          let email = '';
          for (const key in rowData) {
            if (key.toLowerCase().includes('email') && rowData[key]) {
              email = String(rowData[key]);
              break;
            }
          }
          // Fallback to email_emelia if present
          if (!email || !validateEmail(email)) {
            throw new Error(`Email invalide ou manquant: ${email || 'vide'}`);
          }

          // Create prospect object with all the required fields
          return {
            prenom: rowData.firstName || '',
            nom: rowData.lastName || '',
            email: email,
            linkedin: rowData.linkedinUrlProfile || '',
            site_url: rowData.domain_emelia || rowData.site_url || rowData.companyWebsite || rowData.urlCompany || '',
            token: generateToken(),
            page_vue: false,
            quiz_fait: false,
            rdv_pris: false,
            // Store all additional fields in metadata
            metadata: {
              gender_emelia: rowData.gender_emelia,
              fullName: rowData.fullName,
              location: rowData.location,
              isPremium: rowData.isPremium === 'TRUE' || rowData.isPremium === true,
              summary: rowData.summary,
              title: rowData.title,
              company: rowData.company,
              companyLocation: rowData.companyLocation,
              companyDescription: rowData.companyDescription,
              companySize: rowData.companySize,
              estimatedMaxRevenue: rowData.estimatedMaxRevenue,
              estimatedMinRevenue: rowData.estimatedMinRevenue,
              industry: rowData.industry,
              description: rowData.description,
              type: rowData.type
            }
          };
        });

        const { error: supabaseError } = await supabase
          .from('prospects')
          .insert(prospects);

        if (supabaseError) throw supabaseError;

        alert(`✅ ${prospects.length} prospects importés avec succès !`);
        setPreviewData([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'import');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow mb-8">
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FileSpreadsheet className="mr-2" /> Import de prospects
        </h2>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Import de fichier Excel/CSV
          </label>
          
          <input
            type="file"
            className="hidden"
            accept=".xlsx,.xls,.csv"
            ref={fileInputRef}
            onChange={handleFileUpload}
          />
          
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
              Sélectionner un fichier
            </button>
            
            {isUploading && (
              <div className="flex items-center text-sm text-gray-500">
                <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                Chargement en cours...
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Astuce : Assurez-vous que la première ligne contient les noms des colonnes
          </p>
        </div>
        
        {error && (
          <div className={`mt-4 p-4 rounded-md ${error.includes('succès') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {error}
          </div>
        )}
          
        {previewData && previewData.length > 0 && (
          <div className="mt-4">
            <h3 className="text-md font-medium mb-2">Aperçu des données :</h3>
            <div className="overflow-x-auto max-h-96 border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    {previewData[0]?.map((header, i) => (
                      <th
                        key={i}
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {header || 'Colonne ' + (i+1)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.slice(1).map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {row.map((cell, j) => (
                        <td key={j} className="px-4 py-3 text-sm text-gray-500 truncate max-w-xs">
                          {cell === null ? '—' : String(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {previewData && previewData.length > 1 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800 font-medium">
                  <Info className="inline-block mr-1 h-4 w-4" /> 
                  Vous allez importer <strong>tous les prospects</strong> du fichier, pas uniquement ceux affichés dans l'aperçu.
                </p>
              </div>
            )}
            
            <div className="mt-4">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleImport}
                disabled={isUploading || previewData.length <= 1}
              >
                {isUploading ? (
                  <>
                    <RefreshCw className="-ml-1 mr-2 h-5 w-5 animate-spin" />
                    Importation en cours...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="-ml-1 mr-2 h-5 w-5" />
                    {fullData && fullData.length > 1 ? `Importer tous les prospects (${fullData.length - 1})` : 'Importer les données'}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelImportSection;
